import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  TagIcon,
  FilmIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../utils/api';
import { contentApi } from '../services/api';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  contentType: 'movie' | 'series' | 'documentary' | 'live' | 'video' | 'episode';
  mediaType: 'hls' | 'dash' | 'mp4' | 'video';
  mediaUrl?: string;
  mediaFilePath?: string;
  thumbnailUrl?: string;
  durationSeconds: number;
  totalEpisodes?: number;
  episodeNumber?: number;
  tags?: string[];
  status: 'pending' | 'processing' | 'ready' | 'transcription_failed' | 'active' | 'draft' | 'error' | 'archived';
  transcriptionStatus?: string;
  keywordCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface TranscriptionKeyword {
  id: string;
  word: string;
  normalizedWord: string;
  startTime: string;
  endTime: string;
  confidence: string;
  speakerLabel?: string;
  segment?: {
    text: string;
    startTime: number;
    endTime: number;
    vttTime: string;
  } | null;
}

interface TranscriptSegment {
  id: number;
  startTime: number;
  endTime: number;
  text: string;
  confidence: number;
  vttTime: string;
}

const ContentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [keywords] = useState<TranscriptionKeyword[]>([]);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetryingTranscription, setIsRetryingTranscription] = useState(false);
  
  // Anahtar kelime arama ve filtreleme için yeni state'ler
  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordSortBy, setKeywordSortBy] = useState<'word' | 'startTime' | 'confidence'>('startTime');
  const [keywordSortOrder, setKeywordSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  // Transcript viewing states
  const [activeTab, setActiveTab] = useState<'keywords' | 'segments'>('segments');
  const [isDownloadingVTT, setIsDownloadingVTT] = useState(false);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Transcription management states
  const [transcriptions, setTranscriptions] = useState<any[]>([]);
  const [transcriptionsLoading, setTranscriptionsLoading] = useState(false);
  const [isUploadingTranscript, setIsUploadingTranscript] = useState(false);
  const [isStartingTranscription, setIsStartingTranscription] = useState(false);
  const [showTranscriptUpload, setShowTranscriptUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      fetchContent();
    }
  }, [id]);

  // Fetch transcriptions when content is loaded
  useEffect(() => {
    if (content && id) {
      fetchTranscriptions();
    }
  }, [content, id]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{data: ContentItem}>(`/content/${id}`);
      
      if (response.data) {
        setContent(response.data);
        
        // Fetch segments if transcription is completed
        if (response.data.transcriptionStatus === 'completed') {
          fetchTranscriptSegments();
        }
      } else {
        setError('İçerik bulunamadı');
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('İçerik yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchTranscriptSegments = async () => {
    try {
      setSegmentsLoading(true);
      const response = await contentApi.getTranscriptSegments(id!) as any;
      
      if (response.data) {
        setSegments(response.data.segments || []);
      }
    } catch (error) {
      console.error('Error fetching transcript segments:', error);
    } finally {
      setSegmentsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !id) return;
    
    try {
      setSearchLoading(true);
      const response = await contentApi.searchSegments(id, searchQuery.trim()) as any;
      
      if (response.data) {
        setSearchResults(response.data.segments || []);
        setActiveTab('keywords'); // Switch to search results tab
      }
    } catch (error) {
      console.error('Error searching segments:', error);
      setError('Arama sırasında hata oluştu');
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setActiveTab('segments');
  };

  const handleDownloadVTT = async () => {
    if (!content) return;
    
    try {
      setIsDownloadingVTT(true);
      await contentApi.downloadTranscriptVTT(content.id, content.title);
    } catch (error) {
      console.error('Error downloading VTT:', error);
      setError('VTT dosyası indirilirken hata oluştu');
    } finally {
      setIsDownloadingVTT(false);
    }
  };

  const handleDelete = async () => {
    if (!content) return;
    
    try {
      setIsDeleting(true);
      await apiClient.delete(`/content/${content.id}`);
      navigate('/content');
    } catch (error) {
      console.error('Error deleting content:', error);
      setError('İçerik silinirken hata oluştu');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRetryTranscription = async () => {
    if (!content) return;
    
    try {
      setIsRetryingTranscription(true);
      await apiClient.post(`/content/${content.id}/retry-transcription`);
      
      // Refresh content to get updated status
      await fetchContent();
    } catch (error) {
      console.error('Error retrying transcription:', error);
      setError('Transkripsiyon yeniden başlatılırken hata oluştu');
    } finally {
      setIsRetryingTranscription(false);
    }
  };

  const fetchTranscriptions = async () => {
    if (!id) return;
    
    try {
      setTranscriptionsLoading(true);
      const response = await contentApi.listTranscriptions(id) as any;
      
      if (response.data) {
        setTranscriptions(response.data.transcriptions || []);
      }
    } catch (error) {
      console.error('Error fetching transcriptions:', error);
    } finally {
      setTranscriptionsLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!id) return;
    
    try {
      setIsUploadingTranscript(true);
      await contentApi.uploadTranscript(id, file);
      
      // Refresh transcriptions list
      await fetchTranscriptions();
      setShowTranscriptUpload(false);
      setUploadFile(null);
    } catch (error) {
      console.error('Error uploading transcript:', error);
      setError('Transkript yüklenirken hata oluştu');
    } finally {
      setIsUploadingTranscript(false);
    }
  };

  const handleStartTranscription = async () => {
    if (!id) return;
    
    try {
      setIsStartingTranscription(true);
      await contentApi.startTranscription(id);
      
      // Refresh content and transcriptions
      await Promise.all([fetchContent(), fetchTranscriptions()]);
    } catch (error) {
      console.error('Error starting transcription:', error);
      setError('Transkripsiyon başlatılırken hata oluştu');
    } finally {
      setIsStartingTranscription(false);
    }
  };

  const handleSetActiveTranscription = async (transcriptionId: string) => {
    if (!id) return;
    
    try {
      await contentApi.setActiveTranscription(id, transcriptionId);
      
      // Refresh transcriptions and content
      await Promise.all([fetchTranscriptions(), fetchContent()]);
    } catch (error) {
      console.error('Error setting active transcription:', error);
      setError('Aktif transkripsiyon ayarlanırken hata oluştu');
    }
  };

  const formatDuration = (seconds: number, contentType: string): string => {
    if (contentType === 'live') return 'Canlı';
    if (seconds === 0) return '0:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'transcription_failed':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'archived':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'ready':
        return 'Hazır';
      case 'draft':
        return 'Taslak';
      case 'processing':
        return 'İşleniyor';
      case 'pending':
        return 'Bekliyor';
      case 'transcription_failed':
        return 'Transkripsiyon Hatası';
      case 'error':
        return 'Hata';
      case 'archived':
        return 'Arşivlendi';
      default:
        return status;
    }
  };

  const getTypeText = (type: string): string => {
    switch (type) {
      case 'movie':
        return 'Film';
      case 'series':
        return 'Dizi';
      case 'episode':
        return 'Bölüm';
      case 'documentary':
        return 'Belgesel';
      case 'live':
        return 'Canlı';
      case 'video':
        return 'Video';
      default:
        return type;
    }
  };

  const formatTime = (timeStr: string): string => {
    const time = parseFloat(timeStr);
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Anahtar kelime filtreleme ve sıralama
  const filteredAndSortedKeywords = keywords
    .filter(keyword => 
      keyword.word.toLowerCase().includes(keywordSearch.toLowerCase()) ||
      keyword.normalizedWord.toLowerCase().includes(keywordSearch.toLowerCase())
    )
    .sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (keywordSortBy) {
        case 'word':
          aValue = a.word.toLowerCase();
          bValue = b.word.toLowerCase();
          break;
        case 'startTime':
          aValue = parseFloat(a.startTime);
          bValue = parseFloat(b.startTime);
          break;
        case 'confidence':
          aValue = parseFloat(a.confidence);
          bValue = parseFloat(b.confidence);
          break;
        default:
          aValue = parseFloat(a.startTime);
          bValue = parseFloat(b.startTime);
      }
      
      if (keywordSortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Gösterilecek anahtar kelimeler
  const displayedKeywords = showAllKeywords ? filteredAndSortedKeywords : filteredAndSortedKeywords.slice(0, 50);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="h-64 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-red-400">
          <ExclamationTriangleIcon />
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900">Hata Oluştu</h3>
        <p className="mt-1 text-sm text-gray-500">{error || 'İçerik bulunamadı'}</p>
        <div className="mt-6">
          <Link
            to="/content"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            İçerik Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/content"
            className="inline-flex items-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            İçerik Listesi
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">{content.title}</h1>
            <p className="text-sm text-gray-500">İçerik Detayları</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/content/${content.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Düzenle
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Sil
          </button>
        </div>
      </div>

      {/* Content Info */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Thumbnail */}
          <div className="lg:col-span-1">
            <div className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
              {content.thumbnailUrl ? (
                <img
                  src={content.thumbnailUrl}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FilmIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(content.status)}`}>
                  {getStatusText(content.status)}
                </span>
              </div>
              <div className="absolute bottom-2 left-2">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-black bg-opacity-75 text-white">
                  <ClockIcon className="mr-1 h-3 w-3" />
                  {formatDuration(content.durationSeconds, content.contentType)}
                </span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{content.title}</h2>
              <p className="text-gray-600">{content.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-500">İçerik Türü:</span>
                <div className="flex items-center mt-1">
                  <TagIcon className="h-4 w-4 mr-1 text-gray-400" />
                  {getTypeText(content.contentType)}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-500">Medya Türü:</span>
                <div className="mt-1">{content.mediaType}</div>
              </div>
              <div>
                <span className="font-medium text-gray-500">Oluşturulma:</span>
                <div className="mt-1">{new Date(content.createdAt).toLocaleDateString('tr-TR')}</div>
              </div>
              <div>
                <span className="font-medium text-gray-500">Güncellenme:</span>
                <div className="mt-1">{new Date(content.updatedAt).toLocaleDateString('tr-TR')}</div>
              </div>
            </div>

            {content.contentType === 'series' && (
              <div className="text-sm">
                <span className="font-medium text-gray-500">Bölüm Bilgisi:</span>
                <div className="mt-1">
                  Bölüm {content.episodeNumber} / {content.totalEpisodes}
                </div>
              </div>
            )}

            {/* Tags */}
            {content.tags && content.tags.length > 0 && (
              <div>
                <span className="font-medium text-gray-500 text-sm">Etiketler:</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {content.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Media URLs */}
            <div className="space-y-2 text-sm">
              {content.mediaUrl && (
                <div>
                  <span className="font-medium text-gray-500">Medya URL:</span>
                  <div className="mt-1 break-all text-blue-600">
                    <a href={content.mediaUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {content.mediaUrl}
                    </a>
                  </div>
                </div>
              )}
              {content.mediaFilePath && (
                <div>
                  <span className="font-medium text-gray-500">Dosya Yolu:</span>
                  <div className="mt-1 text-gray-600">{content.mediaFilePath}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transcription Status */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Transkripsiyon Durumu</h3>
          {content.status === 'transcription_failed' && (
            <button
              onClick={handleRetryTranscription}
              disabled={isRetryingTranscription}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1 ${isRetryingTranscription ? 'animate-spin' : ''}`} />
              {isRetryingTranscription ? 'Yeniden Başlatılıyor...' : 'Yeniden Dene'}
            </button>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center">
            <span className="font-medium text-gray-500 w-32">Durum:</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(content.transcriptionStatus || content.status)}`}>
              {content.transcriptionStatus ? getStatusText(content.transcriptionStatus) : getStatusText(content.status)}
            </span>
          </div>
          
          {content.keywordCount !== undefined && (
            <div className="flex items-center">
              <span className="font-medium text-gray-500 w-32">Anahtar Kelime:</span>
              <span className="text-gray-900">{content.keywordCount} kelime</span>
            </div>
          )}
        </div>
      </div>

      {/* Transcript Viewer */}
      {content.transcriptionStatus === 'completed' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Transkript Görüntüleyici</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDownloadVTT}
                disabled={isDownloadingVTT}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className={`h-4 w-4 mr-1 ${isDownloadingVTT ? 'animate-spin' : ''}`} />
                {isDownloadingVTT ? 'İndiriliyor...' : 'VTT İndir'}
              </button>
            </div>
          </div>

          {/* Search Interface */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label htmlFor="transcript-search" className="block text-sm font-medium text-gray-700 mb-2">
              Transkriptte Ara
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                id="transcript-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Cümle veya kelime ara..."
                className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <button
                onClick={handleSearch}
                disabled={!searchQuery.trim() || searchLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {searchLoading ? 'Arıyor...' : 'Ara'}
              </button>
              {searchResults.length > 0 && (
                <button
                  onClick={clearSearch}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Temizle
                </button>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                <span className="font-medium">{searchResults.length}</span> sonuç bulundu: "{searchQuery}"
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('keywords')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'keywords'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <TagIcon className="h-4 w-4 mr-2" />
                  Arama Sonuçları
                  {searchResults.length > 0 && (
                    <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {searchResults.length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('segments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'segments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Tüm Cümleler
                  {segments.length > 0 && (
                    <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {segments.length}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>

          {/* Search Results Tab */}
          {activeTab === 'keywords' && (
            <div className="space-y-4">
              {searchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Arama yapılıyor...</span>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.map((result: any, index: number) => (
                    <div key={`search-${result.id}-${index}`} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-sm text-gray-500 font-mono">
                              {result.vttTime}
                            </span>
                            <span className="text-xs text-gray-400">
                              #{result.id}
                            </span>
                          </div>
                          <div className="text-sm text-gray-900 leading-relaxed">
                            <div 
                              dangerouslySetInnerHTML={{ 
                                __html: result.highlightedText || result.text 
                              }}
                              className="search-result-text"
                              style={{
                                lineHeight: '1.6'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery.trim() ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-lg mb-2">🔍</div>
                  <div className="font-medium">Sonuç bulunamadı</div>
                  <div className="text-sm">"{searchQuery}" için eşleşme bulunamadı</div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-lg mb-2">🔎</div>
                  <div className="font-medium">Transkriptte Arama Yapın</div>
                  <div className="text-sm">Yukarıdaki arama kutusunu kullanarak cümle veya kelime arayabilirsiniz</div>
                </div>
              )}
            </div>
          )}

          {/* Old Keywords Implementation - Replaced with Search */}
          {false && activeTab === 'keywords' && (
            <div>
              {false ? (
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
                  ))}
                </div>
              ) : keywords.length > 0 ? (
                <>
                  {/* Arama ve Filtreleme Kontrolleri */}
                  <div className="mb-4 space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <label htmlFor="keyword-search" className="block text-sm font-medium text-gray-700 mb-1">
                          Anahtar Kelime Ara
                        </label>
                        <input
                          type="text"
                          id="keyword-search"
                          value={keywordSearch}
                          onChange={(e) => setKeywordSearch(e.target.value)}
                          placeholder="Kelime ara..."
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div className="sm:w-48">
                        <label htmlFor="sort-by" className="block text-sm font-medium text-gray-700 mb-1">
                          Sıralama
                        </label>
                        <select
                          id="sort-by"
                          value={keywordSortBy}
                          onChange={(e) => setKeywordSortBy(e.target.value as 'word' | 'startTime' | 'confidence')}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="startTime">Zaman</option>
                          <option value="word">Kelime</option>
                          <option value="confidence">Güven</option>
                        </select>
                      </div>
                      <div className="sm:w-32">
                        <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-1">
                          Yön
                        </label>
                        <select
                          id="sort-order"
                          value={keywordSortOrder}
                          onChange={(e) => setKeywordSortOrder(e.target.value as 'asc' | 'desc')}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="asc">↑ Artan</option>
                          <option value="desc">↓ Azalan</option>
                        </select>
                      </div>
                    </div>
                    
                    {keywordSearch && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">{filteredAndSortedKeywords.length}</span> sonuç bulundu
                        {filteredAndSortedKeywords.length !== keywords.length && (
                          <button
                            onClick={() => setKeywordSearch('')}
                            className="ml-2 text-blue-600 hover:text-blue-700"
                          >
                            Filtreyi Temizle
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Kelime
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Zaman
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Güven
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cümle
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {displayedKeywords.map((keyword) => (
                          <tr key={keyword.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {keywordSearch ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: keyword.word.replace(
                                      new RegExp(`(${keywordSearch})`, 'gi'),
                                      '<mark class="bg-yellow-200">$1</mark>'
                                    )
                                  }}
                                />
                              ) : (
                                keyword.word
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="space-y-1">
                                <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                  {formatTime(keyword.startTime)} - {formatTime(keyword.endTime)}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Süre: {((parseFloat(keyword.endTime) - parseFloat(keyword.startTime))).toFixed(1)}s
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                parseFloat(keyword.confidence) > 0.8 
                                  ? 'bg-green-100 text-green-800' 
                                  : parseFloat(keyword.confidence) > 0.6 
                                  ? 'bg-yellow-100 text-yellow-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {(parseFloat(keyword.confidence) * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                              {keyword.segment ? (
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-400 font-mono">
                                    {keyword.segment.vttTime}
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded border">
                                    <span
                                      className="text-sm"
                                      dangerouslySetInnerHTML={{
                                        __html: keyword.segment.text.replace(
                                          new RegExp(`\\b(${keyword.word})\\b`, 'gi'),
                                          '<mark class="bg-blue-100 text-blue-800 font-medium">$1</mark>'
                                        )
                                      }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">Cümle bulunamadı</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {!showAllKeywords && filteredAndSortedKeywords.length > 50 && (
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => setShowAllKeywords(true)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <EyeIcon className="h-4 w-4 mr-2" />
                          Daha Fazla Göster ({filteredAndSortedKeywords.length - 50} kaldı)
                        </button>
                      </div>
                    )}
                    
                    {showAllKeywords && filteredAndSortedKeywords.length > 50 && (
                      <div className="mt-4 text-center text-sm text-gray-500">
                        {filteredAndSortedKeywords.length} anahtar kelime gösteriliyor
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-gray-500">Henüz anahtar kelime bulunamadı.</p>
              )}
            </div>
          )}

          {/* Segments Tab */}
          {activeTab === 'segments' && (
            <div>
              {segmentsLoading ? (
                <div className="animate-pulse space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="border rounded-lg p-4">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-16 bg-gray-200 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              ) : segments.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {segments.map((segment) => (
                    <div key={segment.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className="text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            #{segment.id}
                          </span>
                          <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {segment.vttTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            Süre: {(segment.endTime - segment.startTime).toFixed(1)}s
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            segment.confidence > 0.8 
                              ? 'bg-green-100 text-green-800' 
                              : segment.confidence > 0.6 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {(segment.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 bg-white border rounded p-3">
                        {segment.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Henüz transkript segmenti bulunamadı.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Legacy Keywords Section (keeping for compatibility, but hidden) */}
      {false && content?.transcriptionStatus === 'completed' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Anahtar Kelimeler
              {keywords.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredAndSortedKeywords.length} / {keywords.length})
                </span>
              )}
            </h3>
            {keywords.length > 50 && !showAllKeywords && (
              <button
                onClick={() => setShowAllKeywords(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Tümünü Göster
              </button>
            )}
          </div>
          
          {false ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          ) : keywords.length > 0 ? (
            <>
              {/* Arama ve Filtreleme Kontrolleri */}
              <div className="mb-4 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label htmlFor="keyword-search" className="block text-sm font-medium text-gray-700 mb-1">
                      Anahtar Kelime Ara
                    </label>
                    <input
                      type="text"
                      id="keyword-search"
                      value={keywordSearch}
                      onChange={(e) => setKeywordSearch(e.target.value)}
                      placeholder="Kelime ara..."
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="sm:w-48">
                    <label htmlFor="sort-by" className="block text-sm font-medium text-gray-700 mb-1">
                      Sıralama
                    </label>
                    <select
                      id="sort-by"
                      value={keywordSortBy}
                      onChange={(e) => setKeywordSortBy(e.target.value as 'word' | 'startTime' | 'confidence')}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="startTime">Zaman</option>
                      <option value="word">Kelime</option>
                      <option value="confidence">Güven</option>
                    </select>
                  </div>
                  <div className="sm:w-32">
                    <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-1">
                      Yön
                    </label>
                    <select
                      id="sort-order"
                      value={keywordSortOrder}
                      onChange={(e) => setKeywordSortOrder(e.target.value as 'asc' | 'desc')}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="asc">↑ Artan</option>
                      <option value="desc">↓ Azalan</option>
                    </select>
                  </div>
                </div>
                
                {keywordSearch && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{filteredAndSortedKeywords.length}</span> sonuç bulundu
                    {filteredAndSortedKeywords.length !== keywords.length && (
                      <button
                        onClick={() => setKeywordSearch('')}
                        className="ml-2 text-blue-600 hover:text-blue-700"
                      >
                        Filtreyi Temizle
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kelime
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Başlangıç
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bitiş
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Güven
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Konuşmacı
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedKeywords.map((keyword) => (
                      <tr key={keyword.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {keywordSearch ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: keyword.word.replace(
                                  new RegExp(`(${keywordSearch})`, 'gi'),
                                  '<mark class="bg-yellow-200">$1</mark>'
                                )
                              }}
                            />
                          ) : (
                            keyword.word
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTime(keyword.startTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTime(keyword.endTime)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            parseFloat(keyword.confidence) > 0.8 
                              ? 'bg-green-100 text-green-800' 
                              : parseFloat(keyword.confidence) > 0.6 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {(parseFloat(keyword.confidence) * 100).toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {keyword.speakerLabel || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {!showAllKeywords && filteredAndSortedKeywords.length > 50 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllKeywords(true)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Daha Fazla Göster ({filteredAndSortedKeywords.length - 50} kaldı)
                    </button>
                  </div>
                )}
                
                {showAllKeywords && filteredAndSortedKeywords.length > 50 && (
                  <div className="mt-4 text-center text-sm text-gray-500">
                    {filteredAndSortedKeywords.length} anahtar kelime gösteriliyor
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-500">Henüz anahtar kelime bulunamadı.</p>
          )}
        </div>
      )}

      {/* Transcription Management */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Transkripsiyon Yönetimi</h3>
          <div className="flex items-center space-x-3">
            {content?.mediaUrl && (
              <button
                onClick={handleStartTranscription}
                disabled={isStartingTranscription || content?.status === 'processing'}
                className="inline-flex items-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
              >
                <PlayIcon className={`h-4 w-4 mr-1 ${isStartingTranscription ? 'animate-spin' : ''}`} />
                {isStartingTranscription ? 'Başlatılıyor...' : 'Otomatik Transkripsiyon Başlat'}
              </button>
            )}
            <button
              onClick={() => setShowTranscriptUpload(true)}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded text-blue-700 bg-white hover:bg-blue-50"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-1" />
              Manuel Transkript Yükle
            </button>
          </div>
        </div>

        {/* Upload Modal */}
        {showTranscriptUpload && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Transkript Yükle</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VTT veya SRT dosyası seçin (Maks. 32MB)
                  </label>
                  <input
                    type="file"
                    accept=".vtt,.srt"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowTranscriptUpload(false);
                      setUploadFile(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-400"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => uploadFile && handleFileUpload(uploadFile)}
                    disabled={!uploadFile || isUploadingTranscript}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isUploadingTranscript ? 'Yükleniyor...' : 'Yükle'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transcriptions Table */}
        {transcriptionsLoading ? (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : transcriptions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kaynak
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dosya
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Segment/Kelime
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktif
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transcriptions.map((transcription) => (
                  <tr key={transcription.id} className={`hover:bg-gray-50 ${transcription.isActive ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transcription.source === 'automatic' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {transcription.source === 'automatic' ? 'Otomatik' : 'Manuel'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transcription.status)}`}>
                        {getStatusText(transcription.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transcription.uploadedFileName ? (
                        <div>
                          <div className="font-medium">{transcription.uploadedFileName}</div>
                          <div className="text-xs text-gray-400">{transcription.uploadedFileType?.toUpperCase()}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">AWS Transcribe</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div>{transcription.segmentCount} segment</div>
                        <div>{transcription.keywordCount} kelime</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transcription.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transcription.status === 'completed' ? (
                        transcription.isActive ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <button
                            onClick={() => handleSetActiveTranscription(transcription.id)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                          >
                            Aktif Yap
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <div className="font-medium">Henüz transkripsiyon yok</div>
            <div className="text-sm">Otomatik transkripsiyon başlatın veya manuel transkript yükleyin</div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">İçeriği Sil</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Bu içeriği silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-400"
                >
                  İptal
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Siliniyor...' : 'Sil'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentDetail;